import React, { useState } from "react";
import { Button } from "react-bootstrap";
import { Container, Nav, NavItem, NavLink, Row, Table, UncontrolledTooltip } from "reactstrap";
import BoldButton from "../Common/BoldButton";
import EditViewButton from "../UserAccess/EditViewButton";
import AssignSitesButton from "../UserAccess/AssignSitesButton";
import { CenteredSpinner } from "../Common/CenteredSpinner";
import Confirm from "../Common/Confirm";
import RecipeDto from "../Common/dtos/RecipeDto";
import RecipeUpsertDto from "../Common/dtos/RecipeUpsertDto";
import KaAlert from "../Common/KaAlert";
import KaModal from "../Common/KaModal";
import { KaSelect } from "../Common/KaSelect";
import QueryView from "../Common/QueryView";
import { SiteAssignmentForm } from "../Common/SiteAssignment/SiteAssignmentForm";
import SpinnerButton from "../Common/SpinnerButton";
import useDisplayMessage from "../Common/useDisplayMessage";
import { HttpMethod } from "../Common/useFetch";
import useUserRequest, { UserRequestSpec } from "../Common/useUserRequest";
import FilterInput, { textSatisfiesFilter } from "../FilterInput";
import useAssignRecipesToSites, { AssignmentGroupsDto } from "../Requests/useAssignRecipesToSites";
import useGetRecipes from "../Requests/useGetRecipes";
import useGetRecipesSiteAssignment, { RecipeSiteAssignmentDto, RecipesSiteAssignmentDto } from "../Requests/useGetRecipesSiteAssignment";
import useGetSitesNonAdmin from "../Requests/useGetSitesNonAdmin";
import { useUser } from "../UserAccess/useUser";
import { CreateEditRecipe } from "./CreateEditRecipe";
import { modalMode } from "../Common/EntityModal";
import SiteDto from "../Common/dtos/SiteDto";
import PermissionButton, { permissionDeniedMessage } from "../UserAccess/PermissionButton";
import { usePermission } from "../UserAccess/usePermission";

enum States {
  None,
  AddEditRecipe,
  AssignSites,
}

type PopupState =
  {
    state: States.None;
    recipe?: null;
  } |
  {
    state: States.AddEditRecipe;
    recipe: RecipeDto | null;
  } |
  {
    state: States.AssignSites;
    recipe: RecipeDto;
  };

const apiPath = (businessId?: string): string => `/api/businesses/${businessId}/recipes`;

const apiGetSiteAssignmentPath = (businessId?: string, recipeId?: string): string =>
  `${apiPath(businessId)}/${recipeId}/siteAssignment`;
const apiSetSiteAssignmentPath = (businessId?: string, recipeId?: string): string =>
  `${apiPath(businessId)}/${recipeId}/assignToSites`;

const removeRequestSpec = (businessId?: string, recipeId?: string): UserRequestSpec => ({
  path: `${apiPath(businessId)}/${recipeId}`,
  method: HttpMethod.DELETE,
});

const alphabeticallyByName = (a: RecipeDto, b: RecipeDto) => a.name.localeCompare(b.name);

const RecipePage = () => {
  const user = useUser();
  const displayMessage = useDisplayMessage();
  const [filterText, setFilterText] = useState('');
  const [popupState, setPopupState] = useState<PopupState>({ state: States.None });
  const [recipeToAssignToSites, setRecipeToAssignToSites] = useState<RecipeUpsertDto | null>();
  const [recipeToRemove, setRecipeRemove] = useState<RecipeDto | undefined>(undefined);
  const [recipeSitesViewActive, setRecipeSitesViewActive] = useState(false);
  const canModifyRecipes = usePermission('productsModify');

  const getRecipes = useGetRecipes({
    onSuccess: (data) => {
      if (recipeToAssignToSites && data) {
        const recipeToAssign = data.find(r => r.name === recipeToAssignToSites.name);

        recipeToAssign
          ? assignSitesClick(recipeToAssign)
          : displayMessage.fail(`Something went wrong when trying to find recipe with name ${recipeToAssignToSites.name}`)

        setRecipeToAssignToSites(null);
      }
    }
  });

  const filterTextChanged = (text: string) => setFilterText(text);

  const createClicked = () => {
    displayMessage.clear();
    setPopupState({ state: States.AddEditRecipe, recipe: null });
  }

  const editClick = (recipe: RecipeDto) => {
    displayMessage.clear();
    setPopupState({ state: States.AddEditRecipe, recipe: recipe });
  }

  const createEditSuccess = (recipe: RecipeUpsertDto) => {
    displayMessage.success(`Recipe ${recipe.name} ${popupState.recipe ? 'updated' : 'created'}`);
    getRecipes.query();
  }

  const createEditDismissed = () => setPopupState({ state: States.None });

  const assignSitesClick = (recipe: RecipeDto) => {
    displayMessage.clear();
    setPopupState({ state: States.AssignSites, recipe: recipe });
  }

  const assignSitesSuccess = () => {
    displayMessage.success(`Sites successfully updated for ${popupState.recipe?.name}`);
    assignSitesClosed();
    getRecipes.query();
  }

  const assignSitesClosed = () => setPopupState({ state: States.None });

  const removeSuccess = () => {
    displayMessage.success(`Recipe ${recipeToRemove?.name} removed`);
    getRecipes.query();
  };

  const removeRequest = useUserRequest<void, void>(
    removeRequestSpec(user.selectedBusiness?.id, recipeToRemove?.id), {
    onSuccess: removeSuccess,
    onError: (err) => displayMessage.fail(err.message),
    onComplete: () => setRecipeRemove(undefined),
  });

  const changeToSitesView = (recipesSiteView: boolean) => {
    setRecipeSitesViewActive(recipesSiteView);
    getRecipes.query();
  }

  if (getRecipes.isLoading || removeRequest.isLoading) return <CenteredSpinner />
  if (getRecipes.isError) return <h3>{getRecipes.error?.message}</h3>

  return <Container>
    {popupState.state === States.AddEditRecipe &&
      <CreateEditRecipe
        user={user}
        initialRecipe={popupState.recipe}
        onSuccess={createEditSuccess}
        onDismiss={createEditDismissed}
        mode={modalMode(canModifyRecipes, !popupState.recipe)}
        setRecipeToAssignToSites={setRecipeToAssignToSites} />}
    {popupState.state === States.AssignSites &&
      <SiteAssignmentForm
        title={`Select sites for ${popupState.recipe?.name}`}
        getAssignedSitesPath={apiGetSiteAssignmentPath(user.selectedBusiness?.id, popupState.recipe?.id)}
        assignSitePath={apiSetSiteAssignmentPath(user.selectedBusiness?.id, popupState.recipe?.id)}
        viewOnly={!canModifyRecipes}
        onSuccess={assignSitesSuccess}
        onHide={assignSitesClosed} />}
    <Confirm
      visible={!!recipeToRemove}
      title='Remove recipe'
      body={`When a recipe is removed it is no longer available to dispense. 
               The recipe cannot be restored. 
               Order transactions will not be removed but will no longer link to this recipe.
               Are you sure that you want to remove recipe ${recipeToRemove?.name}?`}
      isLoading={removeRequest.isLoading}
      onConfirm={() => removeRequest.request()}
      onDismiss={() => setRecipeRemove(undefined)} />
    <Row className='justify-content-between mb-2'>
      <h2 className='col-auto ka-blue'>Recipes</h2>
      <PermissionButton permission="productsModify" className='col-auto btn-ghost-primary' onClick={createClicked}>Create Recipe</PermissionButton>
    </Row>
    <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
    <form>
      <div className='row gx-2 gx-md-3 mb-7 justify-content-between'>
        <div className='col-md-4 mb-2 mb-md-0'>
          <FilterInput placeholder='Filter recipes by keyword' onFilterTextChange={filterTextChanged} value={filterText} />
        </div>
        <div className="col-auto">
          <Nav>
            <NavItem>
              <NavLink data-testid='tableView' active={!recipeSitesViewActive} onClick={() => changeToSitesView(false)}>
                <i className="bi-table nav-icon" style={{ "fontSize": 24 }} />
              </NavLink>
            </NavItem>
            <NavItem>
              <NavLink id='swimlaneView' data-testid='swimlaneView' active={recipeSitesViewActive}
                onClick={() => canModifyRecipes && changeToSitesView(true)}
                disabled={getRecipes.data == undefined || getRecipes.data?.length < 1 || !canModifyRecipes}
                className={canModifyRecipes ? undefined : 'pe-auto'}>
                <i className="bi-layout-three-columns nav-icon" style={{ "fontSize": 24 }} />
              </NavLink>
              {!canModifyRecipes &&
                <UncontrolledTooltip target='swimlaneView' placement='top'>{permissionDeniedMessage}</UncontrolledTooltip>}
            </NavItem>
          </Nav>
        </div>
      </div>
    </form>
    {!recipeSitesViewActive
      ? <RecipesView
          filterText={filterText}
          recipes={getRecipes.data}
          setRecipeRemove={setRecipeRemove}
          editClick={editClick}
          assignSitesClick={assignSitesClick} />
      : <RecipesSitesView
          filterText={filterText}
          recipes={getRecipes.data}
          displayMessageSuccess={displayMessage.success}
          displayMessageFail={displayMessage.fail} />}
  </Container>;
};

interface RecipesViewProps {
  filterText: string;
  recipes: RecipeDto[] | null;
  setRecipeRemove: (recipe: RecipeDto | undefined) => void;
  editClick: (recipe: RecipeDto) => void;
  assignSitesClick: (recipe: RecipeDto) => void;
}

const RecipesView = ({
  filterText,
  recipes,
  setRecipeRemove,
  editClick,
  assignSitesClick
}: RecipesViewProps) => {
  const canModify = usePermission('productsModify');

  const containsFilterText = (recipe: RecipeDto) => {
    const satisfiesFilter = textSatisfiesFilter(filterText);
    return satisfiesFilter(recipe.name) || satisfiesFilter(recipe.epaNumber) || satisfiesFilter(recipe.ticketNotes)
      || recipe.sites.find(s => satisfiesFilter(s.name));
  };

  return <Table bordered className="preserve-whitespace">
    <thead>
      <tr>
        <th>Name</th>
        <th>Sites</th>
        <th></th>
      </tr>
    </thead>
    <tbody>
      {recipes?.filter(containsFilterText).sort(alphabeticallyByName).map(recipe =>
        <tr key={recipe.id}>
          <td>{recipe.name}</td>
          <td>
            <span className="badge bg-primary rounded-pill ms-1 mt-2">
              {recipe.sites?.length}
            </span>
          </td>
          <td align="right">
            <div className='dropdown'>
              <Button id='actionsMenuButton'
                className='dropdown-toggle btn-ghost-secondary'
                variant='link'
                data-bs-toggle='dropdown'
                aria-expanded='false'>
                <strong>Actions</strong>
              </Button>
              <div className='dropdown-menu' aria-labelledby='actionsMenuButton'>
                <EditViewButton
                  canModify={canModify}
                  className='dropdown-item btn-ghost-secondary'
                  onClick={() => editClick(recipe)} />
                <AssignSitesButton
                  canModify={canModify}
                  className='dropdown-item btn-ghost-secondary'
                  onClick={() => assignSitesClick(recipe)} />
                <PermissionButton
                  permission="productsModify"
                  className='dropdown-item btn-ghost-secondary'
                  onClick={() => setRecipeRemove(recipe)}>Remove</PermissionButton>
              </div>
            </div>
          </td>
        </tr>)}
    </tbody>
  </Table>
}

interface RecipesSitesViewProps {
  filterText: string;
  recipes: RecipeDto[] | null;
  displayMessageSuccess: (message: string) => void;
  displayMessageFail: (message: string) => void;
}

const RecipesSitesView = ({
  filterText,
  recipes,
  displayMessageSuccess,
  displayMessageFail
}: RecipesSitesViewProps) => {
  const [firstSite, setFirstSite] = useState<SiteDto | null>(null);
  const [firstSiteAssignedRecipes, setFirstSiteAssignedRecipes] = useState<RecipesSiteAssignmentDto>();
  const [firstSiteHasChanges, setFirstSiteHasChanges] = useState(false);
  const [firstSiteNewSelection, setFirstSiteNewSelection] = useState<SiteDto | undefined>(undefined);

  const [secondSite, setSecondSite] = useState<SiteDto | null>(null);
  const [secondSiteAssignedRecipes, setSecondSiteAssignedRecipes] = useState<RecipesSiteAssignmentDto>();
  const [secondSiteHasChanges, setSecondSiteHasChanges] = useState(false);
  const [secondSiteNewSelection, setSecondSiteNewSelection] = useState<SiteDto | undefined>(undefined);

  const [siteToCopyAssignmentsFor, setSiteToCopyAssignmentsFor] = useState<SiteDto | undefined>(undefined);
  const [showCopyAssignments, setShowCopyAssignments] = useState<boolean>(false);

  const containsFilterText = (recipe: RecipeDto) => {
    const satisfiesFilter = textSatisfiesFilter(filterText);
    return satisfiesFilter(recipe.name) || satisfiesFilter(recipe.epaNumber) || satisfiesFilter(recipe.ticketNotes);
  };

  const getSites = useGetSitesNonAdmin({
    onSuccess: resp => {
      if (resp !== undefined && resp.length > 0)
        onFirstSiteChange(resp.sort((a, b) => a.name.localeCompare(b.name)).at(0)!);
    },
  });

  const getFirstSiteRecipeAssignments = useGetRecipesSiteAssignment(firstSite?.id ?? "", {
    autoQuery: false,
    onSuccess: resp => setFirstSiteAssignedRecipes(resp),
    onError: err => displayMessageFail(err.message)
  });

  const getSecondSiteRecipeAssignments = useGetRecipesSiteAssignment(secondSite?.id ?? "", {
    autoQuery: false,
    onSuccess: resp => setSecondSiteAssignedRecipes(resp),
    onError: err => displayMessageFail(err.message)
  });

  const tryChangeFirstSite = (site: SiteDto) => {
    setFirstSiteNewSelection(site);
    setSecondSiteNewSelection(undefined);

    if (!firstSiteHasChanges) onFirstSiteChange(site);
  }

  const tryChangeSecondSite = (site: SiteDto) => {
    setSecondSiteNewSelection(site);
    setFirstSiteNewSelection(undefined);

    if (!secondSiteHasChanges) onSecondSiteChange(site);
  }

  const onFirstSiteChange = (site: SiteDto) => {
    setFirstSite(site);
    setFirstSiteHasChanges(false);
    setFirstSiteNewSelection(undefined);
    if (secondSite === site) setSecondSite(null);
    getFirstSiteRecipeAssignments.query();
  }

  const onSecondSiteChange = (site: SiteDto) => {
    setSecondSite(site);
    setSecondSiteHasChanges(false);
    setSecondSiteNewSelection(undefined);
    if (firstSite === site) setFirstSite(null);
    getSecondSiteRecipeAssignments.query();
  }

  const findRecipeAssignment = (assignments: RecipeSiteAssignmentDto[], recipeId: string): RecipeSiteAssignmentDto | undefined =>
    assignments.find(a => a.recipeId === recipeId);

  const toggleRecipe = (ev: React.ChangeEvent<HTMLInputElement>, forFirstSite: boolean, recipeId: string) => {
    const updatedAssignments: RecipesSiteAssignmentDto = JSON.parse(JSON.stringify(forFirstSite ? firstSiteAssignedRecipes : secondSiteAssignedRecipes));
    findRecipeAssignment(updatedAssignments.assignments, recipeId)!.assigned = ev.target.checked;
    forFirstSite ? setFirstSiteAssignedRecipes(updatedAssignments) : setSecondSiteAssignedRecipes(updatedAssignments);
    forFirstSite ? setFirstSiteHasChanges(true) : setSecondSiteHasChanges(true);
  }

  const updateRecipeAssignments = useAssignRecipesToSites({
    onSuccess: _ => {
      displayMessageSuccess("Recipe assignments updated for " + firstSite?.name + (!!secondSite ? " and " + secondSite?.name : ""));
      setFirstSiteHasChanges(false);
      setSecondSiteHasChanges(false);
    },
    onError: err => displayMessageFail(err.message)
  });

  const saveClicked = () => {
    if (!firstSite || !firstSiteAssignedRecipes) return;

    let dto: AssignmentGroupsDto = {
      assignmentGroups: [{
        siteId: firstSite!.id,
        recipeIds: firstSiteAssignedRecipes!.assignments.filter(a => a.assigned).map(a => a.recipeId)
      }]
    };

    if (!!secondSite && !!secondSiteAssignedRecipes) dto.assignmentGroups.push({
      siteId: secondSite!.id,
      recipeIds: secondSiteAssignedRecipes!.assignments.filter(a => a.assigned).map(a => a.recipeId)
    });

    updateRecipeAssignments.request(dto);
  }

  const copyFromClicked = (site: SiteDto) => {
    setSiteToCopyAssignmentsFor(site);
    setShowCopyAssignments(true);
  }

  const onHideCopyAssignments = () => {
    setShowCopyAssignments(false);
    setSiteToCopyAssignmentsFor(undefined);
  }

  const copyAssignments = (copiedRecipeAssignments: RecipesSiteAssignmentDto) => {
    if (siteToCopyAssignmentsFor === firstSite) {
      setFirstSiteAssignedRecipes(copiedRecipeAssignments);
      setFirstSiteHasChanges(true);
    } else {
      setSecondSiteAssignedRecipes(copiedRecipeAssignments);
      setSecondSiteHasChanges(true);
    }
    setShowCopyAssignments(false);
    setSiteToCopyAssignmentsFor(undefined);
  }

  return <>
    <Confirm
      visible={(!!firstSiteNewSelection && firstSiteHasChanges) || (!!secondSiteNewSelection && secondSiteHasChanges)}
      title={`Unsaved Changes for ${!!firstSiteNewSelection ? firstSite?.name : secondSite?.name}`}
      body={`You have unsaved changes. Please save your work before selecting another location, or you will lose any updates you have made.
               Are you sure that you want to change site?`}
      isLoading={false}
      onDismiss={() => !!firstSiteNewSelection ? setFirstSiteNewSelection(undefined) : setSecondSiteNewSelection(undefined)}
      onConfirm={() => !!firstSiteNewSelection ? onFirstSiteChange(firstSiteNewSelection!) : onSecondSiteChange(secondSiteNewSelection!)} />
    <QueryView
      query={getSites}
      renderData={sites => <div>
        {showCopyAssignments && <CopyAssignmentsModal
          onHide={onHideCopyAssignments}
          show={showCopyAssignments}
          site={siteToCopyAssignmentsFor!}
          options={sites.filter(s => s.id !== siteToCopyAssignmentsFor?.id)
            .sort((a, b) => a.name.localeCompare(b.name))}
          onCopyAssignments={copyAssignments}
        />}
        <Table borderless>
          <thead>
            <tr>
              <th className="col-4"></th>
              <th></th>
              <th className="col-4">
                <KaSelect
                  id="site1"
                  placeholder="Site Selection"
                  value={!!firstSite ? { value: firstSite.id, label: firstSite.name } : undefined}
                  onChange={(site) => { tryChangeFirstSite(sites!.find((s) => s.id === (site as { value: string, label: string })?.value)!); }}
                  options={sites.filter(s => s.id !== secondSite?.id)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(site => ({ value: site.id, label: site.name }))} />
              </th>
              <th></th>
              <th className="col-4">
                <KaSelect
                  id="site2"
                  placeholder="Site Selection"
                  value={!!secondSite ? { value: secondSite.id, label: secondSite.name } : undefined}
                  onChange={(site) => { tryChangeSecondSite(sites!.find((s) => s.id === (site as { value: string, label: string })?.value)!); }}
                  options={sites.filter(s => s.id !== firstSite?.id)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(site => ({ value: site.id, label: site.name }))
                  } />
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td></td>
              <td></td>
              <td className="ps-1">
                <BoldButton className="btn-ghost-primary" disabled={firstSite == null} onClick={() => copyFromClicked(firstSite!)}>Copy From</BoldButton>
              </td>
              <td></td>
              <td className="ps-1">
                <BoldButton className="btn-ghost-primary" disabled={secondSite == null} onClick={() => copyFromClicked(secondSite!)}>Copy From</BoldButton>
              </td>
            </tr>
            {recipes?.filter(containsFilterText).sort(alphabeticallyByName).map(recipe =>
              <tr key={recipe.id}>
                <td className="border">{recipe.name}</td>
                <td></td>
                <td className="border">
                  {getFirstSiteRecipeAssignments.isLoading && <CenteredSpinner />}
                  {!getFirstSiteRecipeAssignments.isLoading &&
                    !!firstSiteAssignedRecipes?.assignments &&
                    findRecipeAssignment(firstSiteAssignedRecipes!.assignments, recipe.id) &&
                    <div className="form-check d-flex justify-content-center">
                      <input
                        type="checkbox"
                        style={{ "height": "2.5em", "width": "2.5em" }}
                        className="form-check-input"
                        id={`cbx${recipe.name}-${firstSite?.name}`}
                        onChange={(ev) => toggleRecipe(ev, true, recipe.id)}
                        disabled={!firstSite}
                        checked={findRecipeAssignment(firstSiteAssignedRecipes!.assignments, recipe.id)?.assigned} />
                    </div>}
                </td>
                <td></td>
                <td className="border">
                  {getSecondSiteRecipeAssignments.isLoading && <CenteredSpinner />}
                  {!getSecondSiteRecipeAssignments.isLoading &&
                    !!secondSiteAssignedRecipes?.assignments &&
                    findRecipeAssignment(secondSiteAssignedRecipes!.assignments, recipe.id) &&
                    <div className="form-check d-flex justify-content-center">
                      <input
                        type="checkbox"
                        style={{ "height": "2.5em", "width": "2.5em" }}
                        className="form-check-input"
                        id={`cbx${recipe.name}-${secondSite?.name}`}
                        onChange={(ev) => toggleRecipe(ev, false, recipe.id)}
                        disabled={!secondSite}
                        checked={findRecipeAssignment(secondSiteAssignedRecipes?.assignments, recipe.id)?.assigned} />
                    </div>}
                  {!secondSiteAssignedRecipes &&
                    <div className="form-check d-flex justify-content-center">
                      <input type="checkbox"
                        style={{ "height": "2.5em", "width": "2.5em" }}
                        className="form-check-input"
                        disabled />
                    </div>}
                </td>
              </tr>)}
          </tbody>
        </Table>
        <div className="d-flex justify-content-end mt-3">
          <SpinnerButton
            className="w-25 m-1 rounded btn-ghost-primary align-self-end"
            spinning={updateRecipeAssignments.isLoading}
            onClick={() => saveClicked()}>
            Save
          </SpinnerButton></div></div>}
    />
  </>
}

interface CopyAssignmentsProps {
  onHide: () => void;
  show: boolean;
  site: SiteDto;
  options: SiteDto[];
  onCopyAssignments: (recipeSiteAssignments: RecipesSiteAssignmentDto) => void;
}

const CopyAssignmentsModal = (props: CopyAssignmentsProps) => {
  const displayMessage = useDisplayMessage();
  const [selectedSite, setSelectedSite] = useState<SiteDto | undefined>(undefined);
  const [selectedSiteAssignedRecipes, setSelectedSiteAssignedRecipes] = useState<RecipesSiteAssignmentDto | undefined>(undefined);

  const getSelectedSiteRecipeAssignments = useGetRecipesSiteAssignment(selectedSite?.id ?? "", {
    autoQuery: false,
    onSuccess: resp => setSelectedSiteAssignedRecipes(resp),
    onError: err => displayMessage.fail(err.message)
  });

  const onSelectedSiteChange = (site: SiteDto) => {
    setSelectedSite(site);
    getSelectedSiteRecipeAssignments.query();
  }

  return (<KaModal
    onHide={props.onHide}
    show={props.show}
    title={`Copy Recipe Site Assignments to ${props.site.name}`}
    body={(<>
      <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
      <KaSelect
        id="copyFromSite"
        placeholder="Site to Copy Assignments From"
        value={!!selectedSite ? { value: selectedSite.id, label: selectedSite.name } : undefined}
        onChange={(site) => { onSelectedSiteChange(props.options!.find((s) => s.id === (site as { value: string, label: string })?.value)!); }}
        options={props.options.map(site => ({ value: site.id, label: site.name }))}
      />
    </>)}
    footer={
      <BoldButton
        className="w-25 m-1 rounded btn-ghost-primary"
        disabled={!selectedSite || !getSelectedSiteRecipeAssignments.isSuccess || !selectedSiteAssignedRecipes}
        onClick={() => props.onCopyAssignments(selectedSiteAssignedRecipes!)}>
        {!!selectedSite ? `Copy Assignments from ${selectedSite!.name}` : 'Copy Assignments'}
      </BoldButton>
    }
  />)
}

export { RecipePage };
